import React, { useState, useEffect } from 'react';
import { groceryAPI } from '../services/api';
import { useCurrentList } from '../context/CurrentListContext';
import { DashboardStats } from '../types';
import DatabaseSeeder from './DatabaseSeeder';
import DatabaseManager from './DatabaseManager';
import GroceryItemTable from './GroceryItemTable';
import UsersSection from './admin/UsersSection';
import FamiliesSection from './admin/FamiliesSection';

interface AdminPageProps {
  onBack: () => void;
}

const StatCard: React.FC<{
  value: number;
  label: string;
  color: string;
  icon: React.ReactNode;
}> = ({ value, label, color, icon }) => (
  <div className={`min-w-0 bg-white rounded-2xl px-3 py-4 sm:px-6 sm:py-5 border border-gray-100 shadow-sm`}>
    <span className={`inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-xl mb-3 ${color}`}>
      {icon}
    </span>
    <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">{value}</div>
    <div className="text-xs sm:text-sm text-gray-500 font-medium truncate">{label}</div>
  </div>
);

const AdminPage: React.FC<AdminPageProps> = ({ onBack }) => {
  const { currentListId, currentList } = useCurrentList();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [usersVersion, setUsersVersion] = useState(0);

  const refreshStats = () => {
    if (!currentListId) { setStats(null); return; }
    groceryAPI.getStats(currentListId).then((res) => {
      if (res.success && res.data) setStats(res.data);
    }).catch(console.error);
  };

  useEffect(() => { refreshStats(); }, [currentListId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 h-16">
            <button
              type="button"
              onClick={onBack}
              className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors duration-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 5l-7 7 7 7"/>
              </svg>
              Retour
            </button>
            <div className="w-px h-5 bg-gray-200" />
            <div className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
              <span className="text-sm font-semibold text-gray-900">Administration</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Stats row */}
        {stats && (
          <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-10">
            <StatCard
              value={stats.totalItems}
              label="Articles au total"
              color="bg-blue-50"
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
                  <rect x="9" y="3" width="6" height="4" rx="1"/>
                </svg>
              }
            />
            <StatCard
              value={stats.boughtItems}
              label="Achetés"
              color="bg-green-50"
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              }
            />
            <StatCard
              value={stats.toBuyItems}
              label="Restants"
              color="bg-amber-50"
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                </svg>
              }
            />
          </div>
        )}

        {/* Accounts management */}
        <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Comptes</h2>
          </div>
          <div className="p-5">
            <UsersSection onUsersChanged={() => setUsersVersion((v) => v + 1)} />
          </div>
        </section>

        {/* Families management */}
        <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-10">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Familles</h2>
          </div>
          <div className="p-5">
            <FamiliesSection usersVersion={usersVersion} />
          </div>
        </section>

        {/* Per-list tools operate on the currently selected list */}
        <p className="text-sm text-gray-500 mb-4">
          Outils ci-dessous appliqués à la liste : <span className="font-semibold text-gray-700">{currentList ? `${currentList.emoji} ${currentList.name}` : 'aucune'}</span>
        </p>

        {/* Two-column layout on desktop */}
        <div className="flex flex-col lg:flex-row gap-8">

          {/* Left column — DB tools */}
          <div className="lg:w-72 flex-shrink-0 flex flex-col gap-6">

            <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Sauvegarde</h2>
              </div>
              <div className="p-5">
                <DatabaseManager listId={currentListId} onImportComplete={refreshStats} />
              </div>
            </section>

            <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Données initiales</h2>
              </div>
              <div className="p-5">
                <DatabaseSeeder />
              </div>
            </section>
          </div>

          {/* Right column — Articles */}
          <div className="flex-1 min-w-0">
            <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Articles</h2>
              </div>
              <div className="p-5">
                <GroceryItemTable listId={currentListId} onItemsChanged={refreshStats} />
              </div>
            </section>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdminPage;
